import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../ui';
import { media } from '../../utils/media';
import { drawerWidthMinimal, drawerWidthResponsive, drawerWidthResponsiveCompact } from '../../utils/constants';

/**
 * ThreeColumnLayout - Reusable 3-column layout system
 * 
 * Layout Structure:
 * - Left Panel: Can be compact (40px) or expanded (full width)
 * - Main Content: Always visible, adapts width based on panel states
 * - Right Panel: Can be open or closed (no compact mode)
 * 
 * Responsive Behavior:
 * - Large screens (>= bigTablet): Both panels can be open simultaneously
 * - Medium screens (tablet to bigTablet): Context-specific behavior
 *   - Course Edit: Only one panel at a time (opening one closes the other)
 *   - Course View: Existing behavior preserved
 * - Small screens (< tablet): Panels appear as overlays
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.leftPanelContent - Content for left panel
 * @param {React.ReactNode} props.mainContent - Content for main area
 * @param {React.ReactNode} props.rightPanelContent - Content for right panel
 * @param {boolean} props.isLeftPanelCompact - Whether left panel is in compact mode
 * @param {boolean} props.isRightPanelOpen - Whether right panel is open
 * @param {Function} props.onRightPanelToggle - Callback when right panel toggle is clicked
 * @param {boolean} props.exclusivePanelsOnMedium - If true, only one panel can be open at a time on medium screens
 * @param {string} props.context - 'view' or 'edit' - determines specific behaviors
 */

// Left Panel - Fixed position sidebar
const LeftPanel = styled.div`
  width: ${(props) => {
    if (props.$isCompact) return `${drawerWidthMinimal}px`;
    return props.$useCompactWidth ? drawerWidthResponsiveCompact : drawerWidthResponsive;
  }};
  background-color: ${theme.colors.NEU_0};
  position: fixed;
  z-index: 2;
  top: ${theme.headerSize};
  left: 0;
  padding-top: 10px;
  height: calc(100vh - ${theme.headerSize});
  overflow: scroll;
  border-right: 1px solid ${theme.colors.NEU_200};
  transition: width 0.2s ease-in-out;

  ${media.lessThan('tablet')} {
    display: none;
  }
`;

// Right Panel - Fixed position sidebar
const RightPanel = styled.div`
  width: ${(props) => {
    return props.$useCompactWidth ? drawerWidthResponsiveCompact : drawerWidthResponsive;
  }};
  background-color: ${theme.colors.NEU_0};
  position: fixed;
  top: ${theme.headerSize};
  right: 0;
  height: calc(100vh - ${theme.headerSize});
  overflow: scroll;
  border-left: 1px solid ${theme.colors.NEU_200};
  transition: width 0.2s ease-in-out;
  display: ${({ $isOpen }) => ($isOpen ? 'inherit' : 'none')};

  /* Hide on small screens (< 768px) for both contexts */
  ${media.lessThan('tablet')} {
    display: none;
  }

  /* Hide on medium screens (768-1024px) ONLY in view context */
  ${({ $context }) => $context === 'view' && `
    ${media.lessThan('bigTablet')} {
      display: none;
    }
  `}
`;

// Main Content Area - Adjusts margins based on panel states
const MainContent = styled.div`
  margin-left: ${({ $isLeftCompact, $bothPanelsOpen }) => {
    if ($isLeftCompact) return `${drawerWidthMinimal}px`;
    return $bothPanelsOpen ? drawerWidthResponsiveCompact : drawerWidthResponsive;
  }};
  position: relative;
  height: calc(100vh - ${theme.headerSize});
  margin-right: 0;
  transition: margin-left 0.2s ease-in-out, margin-right 0.2s ease-in-out;

  /* Apply right margin on large screens (>= 1025px) for both contexts */
  ${media.greaterThan('bigTablet')} {
    margin-right: ${({ $isRightOpen, $bothPanelsOpen }) => {
      if (!$isRightOpen) return 0;
      return $bothPanelsOpen ? drawerWidthResponsiveCompact : drawerWidthResponsive;
    }};
  }

  /* Apply right margin on medium screens (768-1024px) ONLY in edit context */
  ${({ $context, $isRightOpen, $bothPanelsOpen }) => $context === 'edit' && $isRightOpen && `
    ${media.greaterThan('tablet')} {
      margin-right: ${$bothPanelsOpen ? drawerWidthResponsiveCompact : drawerWidthResponsive};
    }
  `}

  ${media.lessThan('tablet')} {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
`;

const ThreeColumnLayout = ({
  leftPanelContent,
  mainContent,
  rightPanelContent,
  isLeftPanelCompact = false,
  isRightPanelOpen = false,
  onRightPanelToggle,
  exclusivePanelsOnMedium = false,
  context = 'view',
}) => {
  // Track window size for medium breakpoint logic
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      // Medium screen: tablet (768) to bigTablet (1025)
      setIsMediumScreen(width >= 768 && width < 1025);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle exclusive panel logic for medium screens
  useEffect(() => {
    if (exclusivePanelsOnMedium && isMediumScreen && !isLeftPanelCompact && isRightPanelOpen) {
      // Both panels are trying to be open on medium screen - close right panel
      if (onRightPanelToggle) {
        onRightPanelToggle(false);
      }
    }
  }, [exclusivePanelsOnMedium, isMediumScreen, isLeftPanelCompact, isRightPanelOpen, onRightPanelToggle]);

  // Determine if both panels are open (only relevant on large screens)
  const bothPanelsOpen = !isLeftPanelCompact && isRightPanelOpen && !isMediumScreen;

  return (
    <>
      {/* Left Panel */}
      <LeftPanel $isCompact={isLeftPanelCompact} $useCompactWidth={bothPanelsOpen}>
        {!isLeftPanelCompact && leftPanelContent}
      </LeftPanel>

      {/* Main Content */}
      <MainContent
        $isLeftCompact={isLeftPanelCompact}
        $isRightOpen={isRightPanelOpen}
        $bothPanelsOpen={bothPanelsOpen}
        $context={context}
      >
        {mainContent}
      </MainContent>

      {/* Right Panel */}
      <RightPanel $isOpen={isRightPanelOpen} $useCompactWidth={bothPanelsOpen} $context={context}>
        {rightPanelContent}
      </RightPanel>
    </>
  );
};

export default ThreeColumnLayout;

